import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Close, HomeOutlined } from '@mui/icons-material';
import { QButton, QIconButton, QModal, QModalActions, QModalContent, QTextField, QTypography } from '@kw/quokka-ui';
import { useSnackbar } from 'notistack';
import ReactCountryFlag from 'react-country-flag';
import { MutatorOptions } from 'swr';
import { useFetchService } from '../../../utils/fetchService';
import { useLocalStorage } from '../../../utils/useLocalStorage';
import { sendHttpRequest } from '../../../utils/network.service';
import { OrganizationSettingCard } from './OrganizationSettingCard';

interface HomeCountryConfigurationCardProps {
  isAdmin: boolean;
  currentHomeCountry?: string;
  lastUpdated?: string;
  mutate: (data?: unknown, opts?: boolean | MutatorOptions<unknown>) => Promise<unknown>;
}

interface Country {
  name: string;
  alpha2_code: string;
  alpha3_code: string;
  numeric_code: string;
}

interface CountryOption {
  label: string;
  value: string;
}

interface HighRiskCountriesResponse {
  highRiskCountries: string[];
  total: number;
}

const DEFAULT_HOME_COUNTRY: CountryOption = {
  label: 'United States of America',
  value: 'US',
};

export const normalizeCountryName = (countryName: string) => countryName.replace(/\s+\(the\)$/i, '');

export const HomeCountryConfigurationCard = ({ isAdmin, currentHomeCountry, lastUpdated, mutate }: HomeCountryConfigurationCardProps) => {
  const organizationHomeCountryCode = currentHomeCountry?.toUpperCase() || DEFAULT_HOME_COUNTRY.value;
  const [savedHomeCountryCode, setSavedHomeCountryCode] = useState(organizationHomeCountryCode);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [pendingHomeCountryCode, setPendingHomeCountryCode] = useState(organizationHomeCountryCode);
  const [countryQuery, setCountryQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [localLastUpdated, setLocalLastUpdated] = useState<string | Date | undefined>(lastUpdated);
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { data: countriesData, error: countriesError } = useFetchService<Country[]>('/list/countryCodes', accessToken);
  // TODO: list-service currently returns the global CMS list. This should remain the effective
  // organization list when per-organization Risky Nations customization is added.
  const { data: highRiskCountriesData, error: highRiskCountriesError } = useFetchService<HighRiskCountriesResponse>(
    '/list/high-risk-countries',
    accessToken,
  );

  const countryOptions = useMemo(() => {
    if (!countriesData?.length) return [DEFAULT_HOME_COUNTRY];

    return countriesData
      .map(country => ({
        label: normalizeCountryName(country.name),
        value: country.alpha2_code.toUpperCase(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [countriesData]);

  const highRiskCountryCodes = useMemo(
    () => new Set((highRiskCountriesData?.highRiskCountries || []).map(countryCode => countryCode.toUpperCase())),
    [highRiskCountriesData],
  );
  const filteredCountryOptions = useMemo(() => {
    const normalizedQuery = countryQuery.trim().toLocaleLowerCase();

    if (!normalizedQuery) return countryOptions;

    return countryOptions.filter(country => country.label.toLocaleLowerCase().includes(normalizedQuery));
  }, [countryOptions, countryQuery]);

  const savedHomeCountry = countryOptions.find(country => country.value === savedHomeCountryCode) || {
    label: savedHomeCountryCode,
    value: savedHomeCountryCode,
  };
  const pendingHomeCountry = countryOptions.find(country => country.value === pendingHomeCountryCode) || null;
  const isPendingHomeCountryHighRisk = highRiskCountryCodes.has(pendingHomeCountryCode);
  const isSaveDisabled =
    isSaving ||
    !pendingHomeCountry ||
    !highRiskCountriesData ||
    Boolean(highRiskCountriesError) ||
    isPendingHomeCountryHighRisk ||
    pendingHomeCountryCode === savedHomeCountryCode;

  useEffect(() => {
    setSavedHomeCountryCode(organizationHomeCountryCode);
  }, [organizationHomeCountryCode]);

  useEffect(() => {
    if (!isEditorOpen) setPendingHomeCountryCode(savedHomeCountryCode);
  }, [isEditorOpen, savedHomeCountryCode]);

  useEffect(() => {
    if (lastUpdated) setLocalLastUpdated(lastUpdated);
  }, [lastUpdated]);

  const closeEditor = () => {
    if (isSaving) return;

    setIsEditorOpen(false);
    setPendingHomeCountryCode(savedHomeCountryCode);
    setCountryQuery('');
  };

  const openEditor = () => {
    setPendingHomeCountryCode(savedHomeCountryCode);
    setCountryQuery('');
    setIsEditorOpen(true);
  };

  const updateHomeCountry = async () => {
    if (isSaveDisabled || !pendingHomeCountry) return;

    setIsSaving(true);
    const result = await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/`,
      method: 'PUT',
      body: { homeCountryCode: pendingHomeCountry.value },
      successMessage: `Home country set to ${pendingHomeCountry.label}.`,
      errorMessage: 'Error updating home country.',
      enqueueSnackbar,
      successOptions: {
        action: snackbarId => (
          <DismissButton aria-label="Dismiss notification" size="small" onClick={() => closeSnackbar(snackbarId)}>
            <Close fontSize="small" />
          </DismissButton>
        ),
      },
    });
    setIsSaving(false);

    if (result.error) return;

    setSavedHomeCountryCode(pendingHomeCountry.value);
    setLocalLastUpdated(new Date());
    setIsEditorOpen(false);
    await mutate();
  };

  return (
    <>
      <OrganizationSettingCard
        title="Home Country"
        description="Used to determine which Server Name Indicator (SNI) geolocations discovered during network analysis of an app are considered foreign. A country currently saved as a Risky Nation must first be removed from that list before it can be configured as your Home Country."
        icon={<HomeOutlined />}
        valueLabel="Home country"
        value={
          <CountryValue>
            <ReactCountryFlag countryCode={savedHomeCountry.value} aria-hidden="true" />
            <span>{savedHomeCountry.label}</span>
          </CountryValue>
        }
        lastUpdated={localLastUpdated}
        onEdit={isAdmin && !countriesError ? openEditor : undefined}
      />

      <QModal
        open={isEditorOpen}
        onClose={closeEditor}
        aria-labelledby="select-home-country-title"
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '420px',
            minWidth: 0,
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 'calc(100vh - 24px)',
            borderRadius: '12px',
            overflow: 'hidden',
          },
        }}
        title={
          <ModalHeader>
            <ModalTitle id="select-home-country-title" variant="h3Medium">
              Select home country
            </ModalTitle>
            <SearchField
              autoFocus
              fullWidth
              value={countryQuery}
              onChange={event => setCountryQuery(event.target.value)}
              placeholder="Search countries..."
              inputProps={{
                'aria-label': 'Search countries',
                'aria-controls': 'home-country-list',
                'aria-expanded': true,
                role: 'combobox',
              }}
            />
          </ModalHeader>
        }
        content={
          <ModalContent>
            <CountryList id="home-country-list" data-testid="home-country-list" role="listbox" aria-label="Countries">
              {filteredCountryOptions.map(option => {
                const isHighRisk = highRiskCountryCodes.has(option.value);
                const isSelected = option.value === pendingHomeCountryCode;

                return (
                  <li key={option.value}>
                    <CountryOptionButton
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={isHighRisk}
                      disabled={isHighRisk}
                      data-selected={isSelected}
                      onClick={() => setPendingHomeCountryCode(option.value)}
                    >
                      <FlagContainer>
                        <ReactCountryFlag countryCode={option.value} aria-hidden="true" />
                      </FlagContainer>
                      <CountryName>{option.label}</CountryName>
                      {isHighRisk && <BlockedCountryLabel>On risky nations list</BlockedCountryLabel>}
                    </CountryOptionButton>
                  </li>
                );
              })}
              {!filteredCountryOptions.length && <NoCountriesFound>No countries found.</NoCountriesFound>}
            </CountryList>
            {highRiskCountriesError && (
              <RiskyNationsError role="alert">Risky Nations could not be loaded. Close the editor and try again.</RiskyNationsError>
            )}
          </ModalContent>
        }
        actions={
          <ModalActions>
            <QButton variant="outlined" onClick={closeEditor} disabled={isSaving}>
              Cancel
            </QButton>
            <QButton variant="filled" onClick={updateHomeCountry} disabled={isSaveDisabled}>
              {isSaving ? 'Saving…' : 'Save'}
            </QButton>
          </ModalActions>
        }
      />
    </>
  );
};

const CountryValue = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 9px;
`;

const ModalTitle = styled(QTypography)`
  display: block;
  font-size: 15px;
  line-height: 21px;
  font-weight: 700;
`;

const ModalHeader = styled('div')`
  padding: 16px 18px 12px;
`;

const SearchField = styled(QTextField)`
  margin-top: 12px;

  .MuiInputBase-root {
    min-height: 40px;
    border-radius: 8px;
  }
`;

const ModalContent = styled(QModalContent)`
  height: min(614px, calc(100vh - 190px));
  min-height: 280px;
  padding: 6px;
`;

const ModalActions = styled(QModalActions)`
  gap: 10px;
  padding: 13px 18px 14px;
  border-top: 1px solid #d7dfe2;

  button {
    min-width: 74px;
  }
`;

const CountryList = styled('ul')`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const CountryOptionButton = styled('button')`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 38px;
  padding: 8px 12px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #10252d;
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible,
  &[data-selected='true'] {
    background: #eef5f5;
  }

  &[data-selected='true'] {
    font-weight: 700;
  }

  &:disabled {
    color: #7d898d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FlagContainer = styled('span')`
  display: inline-flex;
  justify-content: center;
  width: 18px;
  flex: 0 0 18px;
`;

const CountryName = styled('span')`
  min-width: 0;
`;

const BlockedCountryLabel = styled('span')`
  margin-left: auto;
  color: #7d898d;
  font-size: 10px;
  line-height: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  white-space: nowrap;
`;

const NoCountriesFound = styled('li')`
  padding: 18px 12px;
  color: #66777d;
  font-size: 14px;
  line-height: 20px;
`;

const RiskyNationsError = styled('div')`
  margin-top: 8px;
  color: #df2e21;
  font-size: 13px;
  line-height: 18px;
`;

const DismissButton = styled(QIconButton)`
  color: #ffffff;
`;
